import { gql } from '@apollo/client';
import {
  ProjectSlimForList,
  ProjectsSortingParams,
} from 'spw-redux/modules/projects/types';
import {
  TeamSlimForList,
  TeamsSortParams,
} from 'spw-redux/modules/teams/types';
import { IUser } from 'spw-redux/modules/user/types';
import { graphqlClient } from './utils';

export const getTeamsForUser = (
  params: {
    userId: number;
  } & TeamsSortParams
) =>
  graphqlClient.query<{ results: { total: number; teams: TeamSlimForList[] } }>(
    {
      fetchPolicy: 'no-cache',
      query: gql`
        query GetTeamsForUser(
          $page: Int
          $perPage: Int
          $sort: TeamSortOptions
          $userId: ID
        ) {
          results: getTeams(
            page: $page
            perPage: $perPage
            sort: $sort
            filter: { userId: $userId }
          ) {
            total
            teams {
              id
              name
              currentUserRole
              hasLogo
              thumbnailS3Path
              requiresRequestToJoin
              teamAdmins {
                id
                name
              }
              numberOfProjects
              numberOfTeamMembers
              users {
                id
                name
                numberOfProjects
                numberOfTeams
                officeLocation
                professionalRole
              }
            }
          }
        }
      `,
      variables: params,
    }
  );

export const getProjectsForUser = (
  userId: number,
  sort?: ProjectsSortingParams,
  itemsPerPage?: number,
  page?: number
) => {
  const sortParam = { ...sort };

  if (sortParam.isMarkedAsFavoriteByUser) {
    sortParam.projectName = 'ASC';
  }

  return graphqlClient.query<{
    editableProjects: { projects: ProjectSlimForList[]; total: number };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetProjectsForUser(
        $userId: ID!
        $page: Int
        $itemsPerPage: Int
        $sort: ProjectSortOptions
      ) {
        editableProjects(
          userId: $userId
          page: $page
          perPage: $itemsPerPage
          sort: $sort
        ) {
          total
          projects {
            id
            projectName
            projectNumber
            type
            status
            isMarkedAsFavoriteByUser
            hasMissingBenchmarkData
            currentUserRole
            client {
              id
              name
              industry {
                id
                name
              }
            }
            projectOwner {
              id
              name
              isActive
            }
            lastEditor {
              id
              name
            }
            insertedAt
            updatedAt
          }
        }
      }
    `,
    variables: { userId, sort: sortParam, itemsPerPage, page },
  });
};

export const searchUsersByName = (params: {
  organizationId: number;
  name: string;
}) =>
  graphqlClient.query<{ users: IUser[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query SearchUsersByName($organizationId: ID!, $name: String) {
        users(organizationId: $organizationId, name: $name) {
          id
          name
          hasAvatar
          thumbnailS3Path
          isActive
        }
      }
    `,
    variables: params,
  });

export const assignUserAsOrgAdmin = (user: Partial<IUser>) =>
  graphqlClient.mutate<{ user: Pick<IUser, 'id'> }>({
    mutation: gql`
      mutation AssignUserAsOrgAdmin($userId: ID!, $isOrgAdmin: Boolean!) {
        user: assignUserAsOrgAdmin(userId: $userId, isOrgAdmin: $isOrgAdmin) {
          id
        }
      }
    `,
    variables: {
      userId: user.id,
      isOrgAdmin: user.isOrgAdmin,
    },
  });
